<template>
  <div class="mini-chart-container" :class="{ 'dark': isDark }">
    <div class="mini-chart-header">
      <span class="chart-symbol">{{ chartRequest.symbol }}</span>
      <span class="chart-timeframe">{{ chartRequest.timeframe }}</span>
    </div>
    
    <!-- Reuse existing Tc chart component with mini dimensions -->
    <div class="mini-chart-wrapper">
      <tc 
        v-if="chartData && !loading"
        ref="miniChart"
        :data="chartData"
        :width="300"
        :height="150"
        :title-txt="chartRequest.symbol"
        :tf="chartRequest.timeframe"
        :night="isDark"
        :legend-buttons="[]"
        :toolbar="false"
        :chart-config="miniChartConfig"
        :color-back="isDark ? '#1f2937' : '#ffffff'"
        :color-grid="isDark ? '#374151' : '#e5e7eb'"
        :color-text="isDark ? '#d1d4dc' : '#374151'"
        :extensions="[]"
        :overlays="[]"
        :x-settings="{}"
        :key="chartKey"
      />
    </div>
    
    <div class="mini-chart-loading" v-if="loading">
      <div class="loading-spinner"></div>
      <span>Loading chart...</span>
    </div>
    <div class="mini-chart-error" v-if="error">
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script>
import Tc from '../Tc.vue';

export default {
  name: 'MiniChart',
  components: {
    Tc
  },
  props: {
    chartRequest: {
      type: Object,
      required: true,
      default: () => ({
        symbol: '',
        timeframe: '5m'
      })
    },
    isDark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      chartData: null,
      chartKey: 0
    };
  },
  computed: {
    miniChartConfig() {
      return {
        type: 'candlestick',
        volume: false,
        'crosshair': false,
        'date-format': 'HH:mm',
        'price-scale': {
          'auto-scale': true,
          'mode': 'normal'
        }
      };
    }
  },
  async mounted() {
    await this.loadChartData();
  },
  watch: {
    chartRequest: {
      handler() {
        this.loadChartData();
      },
      deep: true
    }
  },
  methods: {
    async loadChartData() {
      this.loading = true;
      this.error = null;
      
      try {
        this.chartData = await this.getChartDataFromParent();
        if (!this.chartData || this.chartData.length === 0) {
          this.error = 'No chart data available';
        }
      } catch (error) {
        console.error('Failed to load chart data:', error);
        this.error = 'Failed to load chart data';
      } finally {
        this.loading = false;
        this.chartKey++; // Force re-render of Tc component
      }
    },

    async getChartDataFromParent() {
      // Try to get chart data from main Chart component
      try {
        // Access chart data through global window or from parent Chart component
        const chartData = window.chartData || window.currentChartData;
        
        if (chartData && Array.isArray(chartData) && chartData.length > 0) {
          // Return: last 50 candles for mini chart
          return chartData.slice(-50);
        }
        
        // If no data found, generate mock data for demonstration
        return this.generateMockData();
      } catch (error) {
        console.warn('Could not access chart data, using mock data:', error);
        return this.generateMockData();
      }
    },

    generateMockData() {
      // Generate realistic mock candle data
      const candles = [];
      const now = Date.now();
      const basePrice = 1000 + Math.random() * 5000;
      
      for (let i = 49; i >= 0; i--) {
        const timestamp = now - (i * 5 * 60 * 1000); // 5-minute intervals
        const variation = (Math.random() - 0.5) * 20;
        const open = basePrice + variation;
        const close = open + (Math.random() - 0.5) * 10;
        const high = Math.max(open, close) + Math.random() * 5;
        const low = Math.min(open, close) - Math.random() * 5;
        const volume = Math.random() * 1000000;
        
        candles.push([timestamp, parseFloat(open.toFixed(2)), parseFloat(high.toFixed(2)), parseFloat(low.toFixed(2)), parseFloat(close.toFixed(2)), parseFloat(volume.toFixed(2))]);
      }
      
      return candles;
    }
  }
};
</script>

<style scoped>
.mini-chart-container {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mini-chart-container.dark {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.mini-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.chart-symbol {
  color: #374151;
}

.chart-timeframe {
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
}

.dark .chart-symbol {
  color: #d1d4dc;
}

.dark .chart-timeframe {
  color: #9ca3af;
  background: #374151;
}

.mini-chart-wrapper {
  width: 100%;
  height: 150px;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}

.dark .mini-chart-wrapper {
  background: #111827;
}

.mini-chart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 150px;
  color: #6b7280;
  font-size: 12px;
}

.mini-chart-error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  color: #ef4444;
  font-size: 12px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.dark .loading-spinner {
  border-color: #374151;
  border-top-color: #60a5fa;
  font-size: 12px;
}
</style>
